import { clsx } from "clsx";
import { format } from "date-fns";
import { createPortal } from "react-dom";

import { STAGE_KIND } from "@/features/stages/utils/kind.ts";
import { formatBRL } from "@/lib/currency.ts";
import { dateOnlyToDate } from "@/lib/format.ts";
import type { Bulletin, BulletinRow } from "../../types.ts";
import { formatDecimalPercent } from "../../utils/format.ts";

function formatDay(value: string): string {
	const date = dateOnlyToDate(value);
	return date ? format(date, "dd/MM/yyyy") : value;
}

function formatShortDay(value: string): string {
	const date = dateOnlyToDate(value);
	return date ? format(date, "dd/MM") : value;
}

function money(cents: number | undefined): string {
	return cents === undefined ? "—" : formatBRL(cents);
}

function BulletinPrintRow({
	row,
	showMoney,
}: {
	row: BulletinRow;
	showMoney: boolean;
}) {
	return (
		<tr
			className={clsx("bulletin-print__row", row.cancelled && "is-cancelled")}
			data-depth={row.depth}
		>
			<td className="bulletin-print__item">{row.itemNumber}</td>
			<td>{STAGE_KIND[row.kind].label}</td>
			<td>
				<span
					className="bulletin-print__name"
					style={{ paddingLeft: `${row.depth * 10}px` }}
				>
					{row.name}
					{row.cancelled && (
						<span className="bulletin-print__cancelled"> (cancelada)</span>
					)}
				</span>
				{row.measuredOn.length > 0 && (
					<span
						className="bulletin-print__dates"
						style={{ paddingLeft: `${row.depth * 10}px` }}
					>
						Medições: {row.measuredOn.map(formatShortDay).join(", ")}
					</span>
				)}
			</td>
			<td className="bulletin-print__number">
				{formatDecimalPercent(row.previousPercent)}
			</td>
			<td className="bulletin-print__number">
				{formatDecimalPercent(row.periodPercent)}
			</td>
			<td className="bulletin-print__number">
				{formatDecimalPercent(row.accumulatedPercent)}
			</td>
			{showMoney && (
				<>
					<td className="bulletin-print__number">{money(row.valueCents)}</td>
					<td className="bulletin-print__number">{money(row.previousCents)}</td>
					<td className="bulletin-print__number">{money(row.periodCents)}</td>
					<td className="bulletin-print__number">
						{money(row.accumulatedCents)}
					</td>
					<td className="bulletin-print__number">
						{money(row.remainingCents)}
					</td>
				</>
			)}
		</tr>
	);
}

export function BulletinPrint({
	bulletin,
	showMoney,
}: {
	bulletin: Bulletin;
	showMoney: boolean;
}) {
	const totals = showMoney && bulletin.valued ? bulletin.totals : undefined;
	const withMoney = !!totals;

	return createPortal(
		<section className="bulletin-print" data-testid="bulletin-print">
			<table className="bulletin-print__table">
				<caption className="bulletin-print__caption">
					<span className="bulletin-print__title">
						Boletim de medição · {bulletin.constructionName}
					</span>
					<span className="bulletin-print__meta">
						Período: {formatDay(bulletin.from)} a {formatDay(bulletin.to)} ·
						Impresso em {format(new Date(), "dd/MM/yyyy HH:mm:ss")}
					</span>
				</caption>
				<thead>
					<tr>
						<th className="bulletin-print__col-item">Item</th>
						<th className="bulletin-print__col-kind">Tipo</th>
						<th>Nome</th>
						<th className="bulletin-print__col-number">% anterior</th>
						<th className="bulletin-print__col-number">% período</th>
						<th className="bulletin-print__col-number">% acumulado</th>
						{withMoney && (
							<>
								<th className="bulletin-print__col-money">Valor</th>
								<th className="bulletin-print__col-money">Anterior</th>
								<th className="bulletin-print__col-money">Período</th>
								<th className="bulletin-print__col-money">Acumulado</th>
								<th className="bulletin-print__col-money">Saldo</th>
							</>
						)}
					</tr>
				</thead>
				<tbody>
					{bulletin.rows.map((row) => (
						<BulletinPrintRow
							key={row.stageId}
							row={row}
							showMoney={withMoney}
						/>
					))}
				</tbody>
				{totals && (
					<tfoot>
						<tr className="bulletin-print__totals">
							<td colSpan={6}>Total</td>
							<td className="bulletin-print__number">
								{formatBRL(totals.valueCents)}
							</td>
							<td className="bulletin-print__number">
								{formatBRL(totals.previousCents)}
							</td>
							<td className="bulletin-print__number">
								{formatBRL(totals.periodCents)}
							</td>
							<td className="bulletin-print__number">
								{formatBRL(totals.accumulatedCents)}
							</td>
							<td className="bulletin-print__number">
								{formatBRL(totals.remainingCents)}
							</td>
						</tr>
					</tfoot>
				)}
			</table>
		</section>,
		document.body,
	);
}
